/* eslint-disable arrow-body-style */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  FormControl,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogContent,
  Box,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  Avatar,
  InputLabel,
  Card,
  CardContent,
  Container,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const Orders = () => {
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // State declarations
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [debouncedProductSearch] = useDebounce(productSearch, 300);

  // Form initial values matching TBS_ORDER_HEADER_MST
  const [initialValues, setInitialValues] = useState({
    CustomerId: '',
    OrderStatus: 'Pending',
    Items: [],
    TotalAmount: 0,
    DeliveryCharge: 0,
    GrandTotal: 0,
    PaymentMode: 'COD',
    PaymentStatus: 'Pending',
    Remarks: '',
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Pincode: '',
  });

  // Calculate order totals matching database schema
  const calculateTotals = useCallback(
    (items) => {
      const totalAmount = items.reduce((sum, item) => sum + item.Rate * item.Quantity, 0);
      const totalAmountWithoutTax = items.reduce((sum, item) => {
        const base = item.Quantity * item.Rate;
        const discount = (base * item.DiscPercent) / 100;
        const taxable = base - discount;
        const tax = (taxable * item.Tax) / 100;
        const final = taxable + tax;

        return sum + final;
      }, 0);
      const deliveryCharge = initialValues.DeliveryCharge;
      const grandTotal = totalAmountWithoutTax + deliveryCharge;

      return { totalAmount, grandTotal };
    },
    [initialValues.DeliveryCharge]
  );

  // Update totals when items or delivery charge changes
  useEffect(() => {
    if (initialValues.Items.length > 0) {
      const { totalAmount, grandTotal } = calculateTotals(initialValues.Items);
      setInitialValues((prev) => ({
        ...prev,
        TotalAmount: totalAmount,
        GrandTotal: grandTotal,
      }));
    }
  }, [initialValues.Items, initialValues.DeliveryCharge, calculateTotals]);

  // Validation schema matching database constraints
  const validationSchema = Yup.object().shape({
    CustomerId: Yup.number().required('Customer is required'),
    OrderStatus: Yup.string().required('Status is required'),
    Items: Yup.array().min(1, 'At least one item is required'),
    TotalAmount: Yup.number().min(0).required(),
    DeliveryCharge: Yup.number().min(0).required(),
    GrandTotal: Yup.number().min(0).required(),
    PaymentMode: Yup.string().required('Payment method is required'),
    PaymentStatus: Yup.string().required('Payment status is required'),
    Address1: Yup.string().required('Address is required'),
    City: Yup.string().required('City is required'),
    State: Yup.string().required('State is required'),
    Pincode: Yup.string().required('Pincode is required'),
  });

  // Filter products based on search
  useEffect(() => {
    if (products.length > 0) {
      const filtered = products.filter(
        (product) =>
          product.Product_Details_Description.toLowerCase().includes(debouncedProductSearch.toLowerCase()) ||
          product.Product_Details_Code.toLowerCase().includes(debouncedProductSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [products, debouncedProductSearch]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const matchesSearch =
        order.Order_Id.toString().includes(debouncedSearch) ||
        order.Customer_Name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        order.Payment_Status?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchesStatus = statusFilter === 'all' || order.Order_Status === statusFilter;
      const matchesPayment = paymentFilter === 'all' || order.Payment_Status === paymentFilter;
      return matchesSearch && matchesStatus && matchesPayment;
    });
  }, [orders, debouncedSearch, statusFilter, paymentFilter]);

  // Data fetching
  const fetchData = useCallback(async () => {
    try {
      showLoading();
      const [ordersRes, productsRes, customersRes] = await Promise.all([
        axios.get('transaction/orders/GetAllOrders'),
        axios.get('Master/Products/GetAllProducts'),
        axios.get('Master/Customer/GetAllCustomer'),
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Failed to load data', 'error');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, openSnackBar]);

  // Initialize data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    fetchData();
  }, [fetchData]);

  // Handle product selection
  const handleAddProduct = (product) => {
    const existingItemIndex = initialValues.Items.findIndex((item) => item.ProductId === product.Product_Details_Id);

    if (existingItemIndex >= 0) {
      const updatedItems = [...initialValues.Items];
      updatedItems[existingItemIndex].Quantity += 1;
      updatedItems[existingItemIndex].TotalPrice =
        updatedItems[existingItemIndex].Rate * updatedItems[existingItemIndex].Quantity;
      setInitialValues((prev) => ({ ...prev, Items: updatedItems }));
    } else {
      setInitialValues((prev) => ({
        ...prev,
        Items: [
          ...prev.Items,
          {
            ProductId: product.Product_Details_Id,
            ProductName: product.Product_Details_Description,
            Rate: product.SellingPrice,
            Quantity: 1,
            TotalPrice: product.SellingPrice,
          },
        ],
      }));
    }
    setOpenProductDialog(false);
    setProductSearch('');
  };

  // // Handle quantity change
  // const handleQuantityChange = (index, newQuantity) => {
  //   if (newQuantity < 1) return;

  //   const updatedItems = [...initialValues.Items];
  //   updatedItems[index].Quantity = newQuantity;
  //   updatedItems[index].TotalPrice = updatedItems[index].Rate * newQuantity;
  //   setInitialValues((prev) => ({ ...prev, Items: updatedItems }));
  // };

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setInitialValues((prev) => {
      const updatedItems = prev.Items.map((item, i) =>
        i === index
          ? {
            ...item, // keep all other item fields
            Quantity: newQuantity,
            TotalPrice: item.Rate * newQuantity,
          }
          : item
      );

      return {
        ...prev,        // keep DeliveryCharge, Discount, etc.
        Items: updatedItems, // only update Items
      };
    });
  };


  // Remove product from order
  const handleRemoveProduct = (index) => {
    const updatedItems = [...initialValues.Items];
    updatedItems.splice(index, 1);
    setInitialValues((prev) => ({ ...prev, Items: updatedItems }));
  };
  const handleSubmit = useCallback(
    async (values) => {
      try {
        showLoading();
        console.log('Submitting with values:', values); // Add this
        console.log('Selected order:', selectedOrder); // Add this

        const orderData = {
          // Add OrderId when in edit mode
          ...(edit && { OrderId: selectedOrder.Order_Id }),
          CustomerId: values.CustomerId,
          OrderStatus: values.OrderStatus,
          Items: values.Items.map((item) => ({
            ProductId: item.Product_Id, // Make sure this is included
            Quantity: item.Quantity,
            Rate: item.Rate,
            TotalPrice: item.TotalPrice,
            TaxPercent: item.Tax,
            DiscPercent: item.DiscPercent,
          })),
          TotalAmount: values.TotalAmount,
          DeliveryCharge: values.DeliveryCharge,
          GrandTotal: values.GrandTotal,
          PaymentMode: values.PaymentMode,
          PaymentStatus: values.PaymentStatus,
          PaidAmount: values.PaymentStatus === 'Paid' ? values.GrandTotal : 0,
          Remarks: values.Remarks,
          Address1: values.Address1,
          Address2: values.Address2,
          City: values.City,
          State: values.State,
          Pincode: values.Pincode,
          UserId: userId,
        };

        console.log('Sending order data:', orderData); // Add this for debugging

        if (edit) {
          await axios.put(`transaction/orders/UpdateOrder/${selectedOrder.Order_Id}`, orderData);
          openSnackBar('Order updated successfully!', 'success');
        } else {
          await axios.post('transaction/orders/CreateOrder', orderData);
          openSnackBar('Order created successfully!', 'success');
        }

        fetchData();
        handleCloseDialog();
      } catch (e) {
        console.error('Error:', e.response?.data); // Add this for debugging
        openSnackBar(e.response?.data?.message || 'Operation failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [edit, selectedOrder, userId, fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Status update
  const updateOrderStatus = useCallback(
    async (orderId, status) => {
      try {
        showLoading();
        await axios.patch(`transaction/orders/UpdateOrderStatus/${orderId}`, { OrderStatus: status });
        fetchData();
        openSnackBar('Order status updated successfully!', 'success');
      } catch (e) {
        openSnackBar(e.response?.data?.message || 'Update failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Payment status update
  const updatePaymentStatus = useCallback(
    async (orderId, status) => {
      try {
        showLoading();
        await axios.patch(`transaction/orders/UpdatePaymentStatus/${orderId}`, { PaymentStatus: status });
        fetchData();
        openSnackBar('Payment status updated successfully!', 'success');
      } catch (e) {
        openSnackBar(e.response?.data?.message || 'Update failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Delete order
  const deleteOrder = useCallback(async () => {
    try {
      showLoading();
      await axios.delete(`transaction/orders/DeleteOrder/${deleteId}`);
      fetchData();
      openSnackBar('Order deleted successfully!', 'success');
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Delete failed', 'error');
    } finally {
      hideLoading();
      handleCloseDelete();
    }
  }, [deleteId, fetchData, showLoading, hideLoading, openSnackBar]);

  // Edit order
  const handleEditClick = useCallback(
    async (order) => {
      try {
        showLoading();
        setSelectedOrder(order);
        console.log(order, 'order')
        const detailsResponse = await axios.get(`transaction/orders/GetOrderById/${order.Order_Id}`);
        const orderDetails = detailsResponse.data;

        setInitialValues({
          CustomerId: order.Customer_Id,
          OrderStatus: order.Order_Status,
          Items: orderDetails.items || [],
          TotalAmount: order.Total_Amount,
          DeliveryCharge: order.Delivery_Charge,
          GrandTotal: order.Grand_Total,
          PaymentMode: order.Payment_Mode === 'Cash on Delivery' ? 'COD' : 'Razorpay',
          PaymentStatus: order.Payment_Status,
          Remarks: order.Remarks || '',
          Address1: order.Address1,
          Address2: order.Address2 || '',
          City: order.City,
          State: order.State,
          Pincode: order.Pincode,
        });

        setEdit(true);
        setOpen(true);
      } catch (e) {
        openSnackBar('Failed to load order details', 'error');
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, openSnackBar]
  );

  // Dialog handlers
  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setEdit(false);
    setSelectedOrder(null);
    setInitialValues({
      CustomerId: '',
      OrderStatus: 'Pending',
      Items: [],
      TotalAmount: 0,
      DeliveryCharge: 0,
      GrandTotal: 0,
      PaymentMode: 'COD',
      PaymentStatus: 'Pending',
      Remarks: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Pincode: '',
    });
  }, []);

  const handleCloseDelete = useCallback(() => {
    setOpenDelete(false);
    setDeleteId(null);
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  const handleDeliveryChange = (e, setFieldValue, values) => {
    const delivery = parseFloat(e.target.value) || 0;
    const subtotal = parseFloat(values.TotalAmount) || 0;
    setFieldValue('DeliveryCharge', delivery);
    setFieldValue('GrandTotal', subtotal + delivery);
  };

  // Memoized table row component
  const OrderRow = React.memo(({ order, onEdit, onDelete }) => (
    <TableRow hover>
      <TableCell>#{order.Order_Id}</TableCell>
      <TableCell>
        <Box display="flex" alignItems="center">
          <Avatar sx={{ width: 32, height: 32, mr: 1 }}>{order.Customer_Name?.charAt(0)}</Avatar>
          <Box>
            <Typography variant="body2">{order.Customer_Name}</Typography>
            <Typography variant="caption" color="textSecondary">
              {order.Mobile_No}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>{formatDate(order.Order_Date)}</TableCell>
      <TableCell>
        <Chip
          label={order.Order_Status}
          size="small"
          color={
            order.Order_Status === 'Delivered' ? 'success' : order.Order_Status === 'Cancelled' ? 'error' : 'primary'
          }
        />
      </TableCell>
      <TableCell>
        <Chip
          label={order.Payment_Status}
          size="small"
          color={order.Payment_Status === 'Paid' ? 'success' : order.Payment_Status === 'Failed' ? 'error' : 'default'}
        />
      </TableCell>
      <TableCell>{formatCurrency(order.Grand_Total)}</TableCell>
      <TableCell align="center">
        <IconButton size="small" color="primary" onClick={() => onEdit(order)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={() => {
            setDeleteId(order.Order_Id);
            setOpenDelete(true);
          }}
        >
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  ));

  return (
    <Container maxWidth="xl" sx={{ pt: 2 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h6" fontWeight="bold">
              Order Management
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setOpen(true)}
              startIcon={<PlaylistAddIcon />}
            >
              Create Order
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Order Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Order Placed">Order Placed</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Delivered">Delivered</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} label="Payment Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Table */}
      <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Order #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Payment</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((order) => (
              <OrderRow key={order.Order_Id} order={order} onEdit={handleEditClick} onDelete={deleteOrder} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      {/* Add/Edit Order Dialog */}
      <Dialog maxWidth="lg" open={open} onClose={handleCloseDialog} fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {edit ? 'Edit Order' : 'Create Order'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
              <Form>
                <Grid container spacing={2} pt={1}>
                  {/* Customer Selection */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Customer</InputLabel>
                      <Select
                        name="CustomerId"
                        value={values.CustomerId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.CustomerId && !!errors.CustomerId}
                      >
                        {customers.map((customer) => (
                          <MenuItem key={customer.CustomerId} value={customer.CustomerId}>
                            {customer.FirstName} ({customer.Mobile})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Order Status */}
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Order Status</InputLabel>
                      <Select name="OrderStatus" value={values.OrderStatus} onChange={handleChange} onBlur={handleBlur}>
                        <MenuItem value="Order Placed">Order Placed</MenuItem>
                        <MenuItem value="Processing">Processing</MenuItem>
                        <MenuItem value="Shipped">Shipped</MenuItem>
                        <MenuItem value="Delivered">Delivered</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Payment Mode */}
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Mode</InputLabel>
                      <Select name="PaymentMode" value={values.PaymentMode} onChange={handleChange} onBlur={handleBlur}>
                        <MenuItem value="COD">Cash on Delivery</MenuItem>
                        <MenuItem value="Razorpay">Online Payment</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Payment Status */}
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Status</InputLabel>
                      <Select
                        name="PaymentStatus"
                        value={values.PaymentStatus}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={values.PaymentMode === 'COD'}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Order Items Section */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Order Items
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 300, overflow: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product Description</TableCell>
                            <TableCell align="right">Rate</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="right">Tax %</TableCell>
                            <TableCell align="right">Discount %</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values.Items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.ProductName}</TableCell>
                              <TableCell align="right">{formatCurrency(item.Rate)}</TableCell>
                              <TableCell align="center">
                                <Box display="flex" alignItems="center" justifyContent="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setInitialValues(values)
                                      handleQuantityChange(index, item.Quantity - 1)
                                    }
                                    }
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography mx={1}>{item.Quantity}</Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setInitialValues(values)
                                      handleQuantityChange(index, item.Quantity + 1)
                                    }
                                    }
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{item.Tax}</TableCell>
                              <TableCell align="right">{item.DiscPercent}</TableCell>
                              {/* <TableCell align="right">{formatCurrency(item.TotalPrice)}</TableCell> */}
                              <TableCell align="right">
                                {formatCurrency(
                                  (item.Quantity * item.Rate) // base amount
                                  - ((item.Quantity * item.Rate) * item.DiscPercent / 100) // discount
                                  + (((item.Quantity * item.Rate) - ((item.Quantity * item.Rate) * item.DiscPercent / 100)) * item.Tax / 100) // tax
                                )}
                              </TableCell>

                              <TableCell align="center">
                                <IconButton size="small" color="error" onClick={() => handleRemoveProduct(index)}>
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      startIcon={<PlaylistAddIcon />}
                      onClick={() => setOpenProductDialog(true)}
                    >
                      Add Product
                    </Button> */}
                  </Grid>

                  {/* Pricing Summary */}
                  <Grid item xs={12} sm={6}>
                    <Paper elevation={0} sx={{ p: 2, border: `1px solid #e0e0e0` }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Pricing Summary
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography>Sub Total:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography>{formatCurrency(values.TotalAmount)}</Typography>
                        </Grid>

                        <Grid item xs={6}>
                          <Typography>Delivery Charges:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography>{formatCurrency(values.DeliveryCharge)}</Typography>
                          {/* <TextField
                            size="small"
                            type="number"
                            name="DeliveryCharge"
                            value={values.DeliveryCharge === 0 ? '' : values.DeliveryCharge}
                            onChange={(e) => handleDeliveryChange(e, setFieldValue, values)}
                            onBlur={handleBlur}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            sx={{ maxWidth: 100 }}
                          /> */}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={6}>
                          <Typography fontWeight="bold">Total:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography fontWeight="bold">{formatCurrency(values.GrandTotal)}</Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Shipping Address */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Shipping Address
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 1"
                      name="Address1"
                      value={values.Address1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.Address1 && !!errors.Address1}
                      helperText={touched.Address1 && errors.Address1}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 2"
                      name="Address2"
                      value={values.Address2}
                      onChange={handleChange}
                      sx={{ mb: 1 }}
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="City"
                          name="City"
                          value={values.City}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.City && !!errors.City}
                          helperText={touched.City && errors.City}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="State"
                          name="State"
                          value={values.State}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.State && !!errors.State}
                          helperText={touched.State && errors.State}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Pincode"
                          name="Pincode"
                          value={values.Pincode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.Pincode && !!errors.Pincode}
                          helperText={touched.Pincode && errors.Pincode}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Remarks */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Remarks"
                      name="Remarks"
                      value={values.Remarks}
                      onChange={handleChange}
                      multiline
                      rows={2}
                    />
                  </Grid>

                  {/* Dialog Actions */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                      <Button variant="outlined" color="secondary" onClick={handleCloseDialog}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color={edit ? 'warning' : 'primary'}>
                        {edit ? 'Update Order' : 'Create Order'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight="bold">
            Select Products
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            placeholder="Search products..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.Product_Details_Id}>
                <Card
                  variant="outlined"
                  onClick={() => handleAddProduct(product)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      {product.Product_Details_Description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Category: {product.Category_Description}
                    </Typography>
                    <Typography variant="body2">Price: {formatCurrency(product.SellingPrice)}</Typography>
                    <Typography variant="body2">Stock: {product.Stock}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete this order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button onClick={handleCloseDelete}>Cancel</Button>
          <Button color="error" onClick={deleteOrder}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;
